/**
 * What the user was refused, read off the address with the app's own route
 * parsers. The server resolves the real workspace and approvers from this; it
 * is only a description of the page, never an authority.
 */
import {
  parsePrettyProjectTabPath,
  parsePrettyWorkspacePath,
  workspaceDetailSectionFromTab,
} from './prettyRoutes';

export interface AccessRequestTarget {
  projectId?: string;
  projectSlug?: string;
  clientSlug?: string;
  workspaceId?: string;
  workspaceSlug?: string;
  section?: string;
  path: string;
  reference?: string;
}

export interface AccessRequestTargetHints {
  /** ERR reference shown on the refusal screen. */
  reference?: string;
  /** Known ids win over the address when the surface already has them. */
  projectId?: string | null;
  workspaceId?: string | null;
  section?: string | null;
}

const PROJECT_ROOT = /^\/projects\/([^/]+)\/([^/]+)(?:\/([^/]+))?/;

function safeDecode(segment: string): string {
  try { return decodeURIComponent(segment); } catch { return segment; }
}

const clean = (value?: string | null) => (value ? value : undefined);

/** Null when neither the address nor the hints name anything that could be requested. */
export function buildAccessRequestTarget(
  pathname: string,
  hints: AccessRequestTargetHints = {},
): AccessRequestTarget | null {
  const base: AccessRequestTarget = { path: pathname, reference: clean(hints.reference) };

  const tab = parsePrettyProjectTabPath(pathname);
  const root = pathname.match(PROJECT_ROOT);
  if (tab || root) {
    const section = tab?.tab ?? (root?.[3] ? safeDecode(root[3]) : undefined);
    return {
      ...base,
      projectId: clean(hints.projectId),
      clientSlug: tab?.workspaceSlug ?? safeDecode(root![1]),
      projectSlug: tab?.projectSlug ?? safeDecode(root![2]),
      section: clean(hints.section) ?? (section && section !== 'overview' ? section : undefined),
    };
  }

  const workspace = parsePrettyWorkspacePath(pathname);
  if (workspace) {
    return {
      ...base,
      workspaceId: clean(hints.workspaceId),
      workspaceSlug: workspace.workspaceSlug,
      section: clean(hints.section) ?? clean(workspaceDetailSectionFromTab(workspace.activeTab)),
    };
  }

  if (hints.projectId) return { ...base, projectId: hints.projectId, section: clean(hints.section) };
  if (hints.workspaceId) return { ...base, workspaceId: hints.workspaceId, section: clean(hints.section) };
  return null;
}
